import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { FormArray, AbstractControl } from '@angular/forms';
import {
  FieldArrayType,
  FieldArrayTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { minItemsValidationMessage } from '../formly-config';
import { RepeatItemDialog } from './repeat-item-dialog';

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}([T ]|$)/;
const DATE_TIME_SPACE_REGEX = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/;

@Component({
  selector: 'app-formly-field-repeat',
  templateUrl: './repeat-dialog.html',
  styleUrl: './repeat-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, MatButton, MatIconButton, MatIcon, MatTooltip],
})
export class FormlyFieldRepeatDialog extends FieldArrayType<FieldArrayTypeConfig> {
  private readonly _dialog = inject(MatDialog);
  private readonly _cdr = inject(ChangeDetectorRef);

  override defaultOptions = {
    validators: {
      minItems: {
        expression: (c: AbstractControl, field?: FormlyFieldConfig) => {
          const props = field?.props;
          const minItems = props?.['minItems'];
          const required = props?.required;

          let min = 0;
          if (typeof minItems === 'number') {
            min = minItems;
          } else if (required) {
            min = 1;
          }

          if (min <= 0) {
            return true;
          }

          const arrayLength =
            (c as FormArray)?.length ??
            (Array.isArray(c?.value) ? c.value.length : 0);
          return arrayLength >= min;
        },
        message: (error: unknown, field: FormlyFieldConfig) =>
          minItemsValidationMessage(error, field),
      },
    },
  };

  private get fieldArrayConfig(): FormlyFieldConfig | undefined {
    const array = this.field.fieldArray;
    if (typeof array === 'function') {
      return array(this.field);
    }
    return array;
  }

  get addText(): string {
    return (this.props['addText'] as string) ?? 'Eintrag hinzufügen';
  }

  get editText(): string {
    return (this.props['editText'] as string) ?? 'bearbeiten';
  }

  get removeText(): string {
    return (this.props['removeText'] as string) ?? 'löschen';
  }

  get dialogTitle(): string {
    return (this.props['dialogTitle'] as string) ?? this.addText;
  }

  get minItemsMessage(): string {
    return minItemsValidationMessage(null, this.field);
  }

  get showMinItemsError(): boolean {
    return !!this.showError && this.formControl.hasError('minItems');
  }

  addItem(): void {
    this.openItem(null);
  }

  editItem(index: number): void {
    this.openItem(index);
  }

  removeItem(index: number): void {
    this.remove(index);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.updateValueAndValidity();
    if (this.field.parent?.formControl) {
      this.field.parent.formControl.updateValueAndValidity();
    }
    this._cdr.markForCheck();
  }

  private normalizeDateTime(val: unknown): unknown {
    if (typeof val === 'string' && DATE_TIME_SPACE_REGEX.test(val)) {
      return val.replace(' ', 'T');
    }
    return val;
  }

  private normalizeModelDates(
    model: Record<string, unknown>,
  ): Record<string, unknown> {
    if (!model) return model;
    const normalized: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(model)) {
      normalized[k] = this.normalizeDateTime(v);
    }
    return normalized;
  }

  private getRootModel(): Record<string, unknown> {
    let current: FormlyFieldConfig = this.field;
    while (current.parent) {
      current = current.parent;
    }
    return (current.model as Record<string, unknown>) ?? {};
  }

  private extractDefaultValues(
    fields: FormlyFieldConfig[],
    targetModel: Record<string, unknown>,
  ): void {
    for (const field of fields) {
      if (field.fieldGroup) {
        this.extractDefaultValues(field.fieldGroup, targetModel);
      } else if (field.key && field.defaultValue !== undefined) {
        const key = String(field.key);
        if (targetModel[key] === undefined) {
          targetModel[key] = this.copyValue(field.defaultValue);
        }
      }
    }
  }

  private getInitialValues(
    fields: FormlyFieldConfig[],
    rootModel: Record<string, unknown>,
    formState: unknown,
  ): Record<string, unknown> {
    const initialValues: Record<string, unknown> = {};
    this.extractDefaultValues(fields, initialValues);

    const context = {
      ...(formState as object),
      formState: { ...(formState as object), rootModel },
      rootModel,
    };

    for (const field of fields) {
      if (!field.key) continue;
      const key = String(field.key);

      if (field.expressions) {
        for (const [exprKey, exprValue] of Object.entries(field.expressions)) {
          if (exprKey === `model.${key}` && typeof exprValue === 'string') {
            try {
              let result: unknown;
              if (/^formState\.rootModel\?\.\w+$/.test(exprValue)) {
                const targetKey = exprValue.split('.').pop();
                result = targetKey ? rootModel[targetKey] : undefined;
              } else {
                const evalFn = new Function(
                  'formState',
                  'model',
                  'field',
                  `return ${exprValue};`,
                );
                result = evalFn(context.formState, initialValues, field);
              }

              if (result !== undefined && result !== null && result !== '') {
                initialValues[key] = this.normalizeDateTime(result);
              }
            } catch {
              // Expression evaluation fallback
            }
          }
        }
      }
    }

    return initialValues;
  }

  private sanitizeFieldsForDialog(
    fields: FormlyFieldConfig[],
  ): FormlyFieldConfig[] {
    return fields.map((field) => {
      const cleanedField = this.cleanFieldConfig(field);

      if (cleanedField.expressions) {
        const sanitizedExpressions = { ...cleanedField.expressions };
        for (const key of Object.keys(sanitizedExpressions)) {
          if (key.startsWith('model.')) {
            delete sanitizedExpressions[key];
          }
        }
        cleanedField.expressions = sanitizedExpressions;
      }

      if (cleanedField.fieldGroup) {
        cleanedField.fieldGroup = this.sanitizeFieldsForDialog(
          cleanedField.fieldGroup,
        );
      }

      return cleanedField;
    });
  }

  private stripModelExpressions(fields: FormlyFieldConfig[] | undefined): void {
    if (!fields) return;
    for (const field of fields) {
      if (field.expressions) {
        for (const key of Object.keys(field.expressions)) {
          if (key.startsWith('model.')) {
            delete field.expressions[key];
          }
        }
      }
      if (field.fieldGroup) {
        this.stripModelExpressions(field.fieldGroup);
      }
    }
  }

  private openItem(index: number | null): void {
    const template = this.fieldArrayConfig;
    if (!template || !template.fieldGroup) {
      return;
    }

    const rootModel = this.getRootModel();

    if (this.options?.formState) {
      this.options.formState.rootModel = rootModel;
    }

    const sanitizedFields = this.sanitizeFieldsForDialog(template.fieldGroup);

    let values: Record<string, unknown>;
    if (index === null) {
      values = this.getInitialValues(
        template.fieldGroup,
        rootModel,
        this.options.formState,
      );
    } else {
      const raw =
        this.model[index] ?? this.formControl.at(index)?.getRawValue() ?? {};
      values = this.normalizeModelDates(
        this.copyValue(raw) as Record<string, unknown>,
      );
    }

    const formStateWithRoot = {
      ...this.options.formState,
      rootModel,
    };

    this._dialog
      .open(RepeatItemDialog, {
        data: {
          fields: sanitizedFields,
          model: values,
          formState: formStateWithRoot,
          title: this.dialogTitle,
        },
        width: '640px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }

        const normalizedResult = this.normalizeModelDates(
          result as Record<string, unknown>,
        );

        if (this.options?.formState) {
          this.options.formState.rootModel = this.getRootModel();
        }

        if (index === null) {
          this.add(undefined, normalizedResult);

          const newIndex = (this.field.fieldGroup?.length ?? 1) - 1;
          const newField = this.field.fieldGroup?.[newIndex];
          if (newField) {
            if (newField.fieldGroup) {
              this.stripModelExpressions(newField.fieldGroup);
            }
            this.stripModelExpressions([newField]);
          }

          if (Array.isArray(this.model)) {
            this.model[newIndex] = { ...normalizedResult };
          }

          const itemControl = this.formControl.at(newIndex);
          if (itemControl) {
            itemControl.patchValue(normalizedResult);
            itemControl.markAsDirty();
            itemControl.markAsTouched();
            itemControl.updateValueAndValidity();
          }
        } else {
          const existingField = this.field.fieldGroup?.[index];
          if (existingField) {
            if (existingField.fieldGroup) {
              this.stripModelExpressions(existingField.fieldGroup);
            }
            this.stripModelExpressions([existingField]);
          }

          if (Array.isArray(this.model)) {
            this.model[index] = { ...normalizedResult };
          }

          const itemControl = this.formControl.at(index);
          if (itemControl) {
            itemControl.patchValue(normalizedResult);
            itemControl.markAsDirty();
            itemControl.markAsTouched();
            itemControl.updateValueAndValidity();
          } else {
            this.remove(index);
            this.add(index, normalizedResult);
          }
        }

        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        this.formControl.updateValueAndValidity();
        if (this.field.parent?.formControl) {
          this.field.parent.formControl.updateValueAndValidity();
        }

        this._cdr.markForCheck();
      });
  }

  summary(index: number): string {
    const template = this.props['summary'] as string | undefined;
    return template
      ? this.renderTemplate(template, index)
      : this.allValues(index);
  }

  summaryDetail(index: number): string {
    const template = this.props['summaryDetail'] as string | undefined;
    return template ? this.renderTemplate(template, index) : '';
  }

  private renderTemplate(template: string, index: number): string {
    const withoutEmptyGroups = template.replace(
      /\(([^()]*)\)/g,
      (group, inner: string) => {
        const keys = [...inner.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);
        const allEmpty = keys.every(
          (key) => this.displayValue(key, index) === '',
        );
        return keys.length > 0 && allEmpty ? '' : group;
      },
    );

    return withoutEmptyGroups
      .replace(/\{(\w+)\}/g, (_match, key: string) =>
        this.displayValue(key, index),
      )
      .replace(/\s+/g, ' ')
      .replace(/^[\s\-,/]+|[\s\-,/]+$/g, '')
      .trim();
  }

  private allValues(index: number): string {
    const value = (this.model as Record<string, unknown>[])?.[index] ?? {};
    return Object.keys(value)
      .map((key) => this.displayValue(key, index))
      .filter((entry) => entry.length > 0)
      .join(', ');
  }

  private displayValue(key: string, index: number): string {
    const itemModel = (this.model as Record<string, unknown>[])?.[index] ?? {};
    const value = this.normalizeDateTime(itemModel[key]);

    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (value instanceof Date) {
      return this.formatDate(value);
    }

    if (typeof (value as { toDate?: () => Date })?.toDate === 'function') {
      const date = (value as { toDate: () => Date }).toDate();
      if (date instanceof Date && !Number.isNaN(date.getTime())) {
        return this.formatDate(date);
      }
    }

    if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return this.formatDate(date, value);
      }
    }

    if (typeof value === 'boolean') {
      return value ? 'ja' : 'nein';
    }

    const fieldConfig = this.findField(this.fieldArrayConfig?.fieldGroup, key);
    const rawOptions = fieldConfig?.props?.['options'];
    let options: Record<string, unknown>[] | undefined = Array.isArray(
      rawOptions,
    )
      ? (rawOptions as Record<string, unknown>[])
      : undefined;

    if (!options && fieldConfig?.expressions?.['props.options']) {
      const expr = fieldConfig.expressions['props.options'];
      if (typeof expr === 'string') {
        try {
          const context = {
            ...(this.options?.formState as object),
            formState: this.options?.formState,
          };
          const evalFn = new Function('formState', `return ${expr};`);
          options = evalFn(context.formState) as Record<string, unknown>[];
        } catch {
          // Fallback if expression evaluation fails
        }
      }
    }

    if (Array.isArray(options)) {
      const valueProp = (fieldConfig?.props?.['valueProp'] as string) ?? 'id';
      const labelProp = (fieldConfig?.props?.['labelProp'] as string) ?? 'name';
      const matched = options.find((opt) => opt[valueProp] === value);
      if (matched && matched[labelProp] !== undefined) {
        return String(matched[labelProp]);
      }
    }

    return String(value);
  }

  private findField(
    fields: FormlyFieldConfig[] | undefined,
    key: string,
  ): FormlyFieldConfig | undefined {
    if (!fields) return undefined;
    for (const field of fields) {
      if (field.key === key) return field;
      if (field.fieldGroup) {
        const found = this.findField(field.fieldGroup, key);
        if (found) return found;
      }
    }
    return undefined;
  }

  private formatDate(date: Date, rawStr?: string): string {
    if (rawStr && rawStr.includes('T')) {
      const [datePart, timePart] = rawStr.split('T');
      if (timePart) {
        const [year, month, day] = datePart.split('-');
        const time = timePart.substring(0, 5);
        return `${day}.${month}.${year} ${time}`;
      }
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  private cleanFieldConfig(field: FormlyFieldConfig): FormlyFieldConfig {
    return {
      ...field,
      ...(field.props ? { props: { ...field.props } } : {}),
      ...(field.expressions ? { expressions: { ...field.expressions } } : {}),
    };
  }

  private copyValue<T>(value: T): T {
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (value instanceof Date) {
      return new Date(value.getTime()) as T;
    }
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
